import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { ArrowLeft, Shield, BookOpen, Users, Calendar, Heart, Building2 } from 'lucide-react';

/**
 * WELCOME LETTERS (BUILD 8)
 * 
 * Role-specific welcome letters with:
 * - Calm, clear, direct tone
 * - Non-marketing, non-legal, non-threatening
 */

const WELCOME_CONTENT = {
  institution: {
    title: 'Welcome to LikeKind',
    subtitle: 'For Institutions',
    icon: Building2,
    color: 'var(--lk-earth-brown)',
    paragraphs: [
      'Thank you for creating an institution on LikeKind. This space is designed to help you coordinate, communicate, and connect with your community — without the noise of traditional social platforms.',
      'Your institution profile is not a social media page. There are no feeds, no likes, no comments. Just the tools you need to serve your community effectively.',
      'Every institution requires two stewards. This dual-steward requirement ensures that no single person has unchecked control. Critical actions — like deleting the institution or changing visibility — require approval from both stewards.'
    ],
    whatNext: [
      'Complete your institution profile with a logo and description',
      'Invite an Assistant Steward (required before fully active)',
      'Set your visibility preference (public or private)',
      'Create your first calendar event',
      'Invite members to join'
    ]
  },
  school: {
    title: 'Welcome to LikeKind',
    subtitle: 'For Schools',
    icon: BookOpen,
    color: 'var(--lk-earth-brown)',
    paragraphs: [
      'Thank you for creating a School Commons on LikeKind. This space is designed to help you coordinate classes, communicate with parents, and manage your school community — with student safety as the foundation.',
      'Student safety is built into every feature. Students cannot be discovered by strangers. Student-to-student messaging is disabled by default. Parents can see everything their children receive.',
      'Your school requires two stewards. This ensures accountability and prevents any single person from having unchecked control over student data and school operations.'
    ],
    whatNext: [
      'Invite an Assistant Steward (required)',
      'Create your class groups',
      'Set up your school calendar',
      'Invite teachers and assign them to classes',
      'Invite parents to connect with their children\'s notebooks'
    ],
    safetyReminder: 'Student safety features are always free. We never paywall protection.'
  },
  parent: {
    title: 'Welcome to LikeKind',
    subtitle: 'For Parents',
    icon: Heart,
    color: 'var(--lk-secondary-orange)',
    paragraphs: [
      'Thank you for joining LikeKind as a parent. This space gives you visibility into your child\'s school activities — without intrusion or surveillance.',
      'You can see everything in your child\'s Student Notebook: homework, announcements, permission slips, and messages from teachers. Your child knows when you\'re viewing, so there\'s transparency in both directions.',
      'Your child cannot be discovered by strangers. They cannot message other students. These protections are built into the platform and cannot be disabled.'
    ],
    whatNext: [
      'Connect to your child\'s school',
      'View their Student Notebook',
      'Check the school calendar for upcoming events',
      'Acknowledge permission slips and important notices',
      'Contact teachers directly if needed'
    ],
    safetyReminder: 'Your child\'s safety is the foundation of everything we build. These protections are free and non-negotiable.'
  },
  teacher: {
    title: 'Welcome to LikeKind',
    subtitle: 'For Teachers',
    icon: Users,
    color: 'var(--lk-primary-green)',
    paragraphs: [
      'Thank you for joining as a teacher on LikeKind. This space helps you communicate with students and parents through Student Notebooks — a one-way communication channel designed for safety.',
      'You can send homework assignments, class notes, announcements, permission slips, and report cards to students. Everything you send is visible to parents. Students can acknowledge and respond, but all responses are visible to parents and school stewards.',
      'Student-to-student messaging is disabled. You cannot accidentally enable it. This is a fundamental safety feature of the platform.'
    ],
    whatNext: [
      'Review your assigned classes',
      'Send your first Student Notebook item',
      'Create class events on the school calendar',
      'Communicate with parents as needed'
    ],
    safetyReminder: 'Remember: Everything you send is visible to parents. This transparency protects everyone.'
  }
};

export default function WelcomeLetter() {
  const navigate = useNavigate();
  const { role } = useParams();
  
  const content = WELCOME_CONTENT[role] || WELCOME_CONTENT.institution;
  const Icon = content.icon;

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
      {/* Header */}
      <header className="sticky top-0 z-50" style={{ backgroundColor: 'var(--fy-surface-card)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-2xl mx-auto px-4">
          <div className="flex items-center h-14">
            <button onClick={() => navigate(-1)} className="p-2 -ml-2 rounded-full hover:bg-black/5">
              <ArrowLeft className="w-5 h-5" style={{ color: 'var(--fy-text-primary)' }} />
            </button>
            <div className="flex-1 text-center">
              <h1 className="font-semibold" style={{ color: 'var(--fy-text-primary)' }}>Welcome</h1>
            </div>
            <div className="w-9" />
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        {/* Title Section */}
        <div className="text-center mb-8">
          <div 
            className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
            style={{ backgroundColor: `${content.color}20` }}
          >
            <Icon className="w-8 h-8" style={{ color: content.color }} />
          </div>
          <h1 className="text-2xl font-bold mb-1" style={{ fontFamily: 'Outfit', color: 'var(--fy-text-primary)' }}>
            {content.title}
          </h1>
          <p className="text-sm" style={{ color: content.color }}>{content.subtitle}</p>
        </div>

        {/* Main Content */}
        <div className="space-y-4 mb-8">
          {content.paragraphs.map((paragraph, idx) => (
            <p key={idx} className="text-sm leading-relaxed" style={{ color: 'var(--fy-text-secondary)' }}>
              {paragraph}
            </p>
          ))}
        </div>

        {/* What's Next */}
        <Card className="p-6 mb-6" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}>
          <h2 className="font-semibold mb-4" style={{ color: 'var(--fy-text-primary)' }}>What&apos;s next</h2>
          <ul className="space-y-3">
            {content.whatNext.map((item, idx) => (
              <li key={idx} className="flex items-start gap-3 text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
                <span 
                  className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold"
                  style={{ backgroundColor: content.color, color: 'white' }}
                >
                  {idx + 1}
                </span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </Card>

        {/* Safety Reminder */}
        {content.safetyReminder && (
          <Card className="p-4" style={{ backgroundColor: 'var(--lk-sage-muted)', border: '1px solid var(--lk-primary-green)' }}>
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--lk-primary-green)' }} />
              <p className="text-sm" style={{ color: 'var(--lk-primary-green)' }}>
                {content.safetyReminder}
              </p>
            </div>
          </Card>
        )}

        {/* Continue Button */}
        <div className="mt-8">
          <Button
            onClick={() => navigate('/myworld')}
            className="w-full h-12 rounded-xl"
            style={{ backgroundColor: content.color, color: 'white' }}
          >
            Continue to Base Camp
          </Button>
        </div>
      </main>
    </div>
  );
}
